import React from "react";
import BackgroundCover from "../../components/backgroundCover/BackgroundCover";
import TimelineSection from "../../components/TimelineSection/TimelineSection";
import Title from "../../components/Title/Title";
import { useLanguage } from "../../context/LanguageContext";
import { getTourmalinesTranslations } from "../../translations/tourmalinesTemplate";
import { isMobile, isMobileOrTablet } from "../../utils/appUtils";
import { useEditMode } from "../../context/EditModeContext";
import EditableText from "../../components/EditableText/EditableText";
import { usePageContent } from "../../hooks/usePageContent";
import { TourmalinesTranslations } from "../../translations/tourmalinesTemplate";

function Tourmaline() {
  const imagePrefix = "tourmaline";
  const { language } = useLanguage();
  const { isEditMode } = useEditMode();
  const { content, loading } = usePageContent<TourmalinesTranslations>('tourmalines');

  // Use translations as fallback content
  const t = content || getTourmalinesTranslations(language);
  
  // Add debugging
  console.log('Tourmaline content loaded:', { 
    hasContent: !!content, 
    language, 
    section3: t.section3,
    source: content ? 'JSON' : 'Default translations'
  });

  // Show loading state if needed
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="">
      <BackgroundCover
        backgroundHeight={isMobileOrTablet() ? (isMobile() ? 400 : 600) : undefined}
        imagePosition={isMobileOrTablet() ? "center" : "initial"}
        backgroundImage={`/images/tourmalines_bg.jpg`}
      >
        <div className="absolute top-1/2 left-[10%] -translate-y-1/2 z-10 hidden md:block">
          <Title
            firstLine={isEditMode ? (
              <EditableText
                contentKey="tourmalines.firstLine"
                defaultValue={t.firstLine}
              />
            ) : t.firstLine}
            secondLine={isEditMode ? (
              <EditableText
                contentKey="tourmalines.secondLine"
                defaultValue={t.secondLine}
              />
            ) : t.secondLine}
            className="text-white hidden md:block"
            animate={true}
          />
        </div>
      </BackgroundCover>

      <div className="mx-auto px-4 md:px-0">
        <div className="block md:hidden mb-8 mt-8">
          <Title
            firstLine={isEditMode ? (
              <EditableText
                contentKey="tourmalines.firstLine"
                defaultValue={t.firstLine}
              />
            ) : t.firstLine}
            secondLine={isEditMode ? (
              <EditableText
                contentKey="tourmalines.secondLine"
                defaultValue={t.secondLine}
              />
            ) : <><strong>{t.secondLine}</strong></>}
            className=""
            animate={true}
          />
        </div>
      </div>

      <div className="w-full bg-gradient-to-b from-gray-100 to-gray-200">
        <video
          src="/video/tourmaline.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="w-full max-w-none"
        />
      </div>
      <div className="relative w-full overflow-hidden">
        <div className="w-full bg-gradient-to-b from-[#E1D8D3] via-[#FFFFFF] to-[#E1D8D3] py-12">
          <div className="mx-auto px-4 md:px-0">
            <div className="mx-auto px-4 md:px-12 pt-8 space-y-12 max-w-7xl">
              <TimelineSection
                title={{
                  firstLine: isEditMode ? (
                    <EditableText
                      contentKey="tourmalines.section1.firstLine"
                      defaultValue={t.section1.firstLine}
                    />
                  ) : t.section1.firstLine,
                  secondLine: isEditMode ? (
                    <EditableText
                      contentKey="tourmalines.section1.secondLine"
                      defaultValue={t.section1.secondLine}
                    />
                  ) : t.section1.secondLine,
                }}
                description={
                  isEditMode ? (
                    <div className="space-y-4">
                      {t.section1.description.map((paragraph: string, index: number) => (
                        <EditableText
                          key={index}
                          contentKey={`tourmalines.section1.description.${index}`}
                          defaultValue={paragraph}
                          multiline={true}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {t.section1.description.map((paragraph: string, index: number) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                    </div>
                  )
                }
                media={{
                  type: 'image',
                  src: [`/images/tourmalines_sub_1.jpg`],
                  alt: "Viking Compass"
                }}
                mediaPosition="right"
              />

              <TimelineSection
                title={{
                  firstLine: isEditMode ? (
                    <EditableText
                      contentKey="tourmalines.section2.firstLine"
                      defaultValue={t.section2.firstLine}
                    />
                  ) : t.section2.firstLine,
                  secondLine: isEditMode ? (
                    <EditableText
                      contentKey="tourmalines.section2.secondLine"
                      defaultValue={t.section2.secondLine}
                    />
                  ) : t.section2.secondLine,
                }}
                description={
                  isEditMode ? (
                    <div className="space-y-4">
                      {t.section2.description.map((paragraph: string, index: number) => (
                        <EditableText
                          key={index}
                          contentKey={`tourmalines.section2.description.${index}`}
                          defaultValue={paragraph}
                          multiline={true}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {t.section2.description.map((paragraph: string, index: number) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                    </div>
                  )
                }
                media={{
                  type: 'image',
                  src: [`/images/tourmalines_sub_2.jpg`],
                  alt: "Symbolic Meaning"
                }}
                mediaPosition="left"
                showLine={true}
              />

              {/* Section 3 */}
              {t.section3 && (
                <TimelineSection
                  key="section3"
                  title={{
                    firstLine: isEditMode ? (
                      <EditableText
                        contentKey="tourmalines.section3.firstLine"
                        defaultValue={t.section3.firstLine}
                      />
                    ) : t.section3.firstLine,
                    secondLine: isEditMode ? (
                      <EditableText
                        contentKey="tourmalines.section3.secondLine"
                        defaultValue={t.section3.secondLine}
                      />
                    ) : t.section3.secondLine,
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section3.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section3.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section3.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_3.jpg`],
                    alt: `Section 3 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 4 */}
              {t.section4 && (
                <TimelineSection
                  key="section4"
                  title={{
                    firstLine: isEditMode ? (
                      <EditableText
                        contentKey="tourmalines.section4.firstLine"
                        defaultValue={t.section4.firstLine}
                      />
                    ) : t.section4.firstLine,
                    secondLine: isEditMode ? (
                      <EditableText
                        contentKey="tourmalines.section4.secondLine"
                        defaultValue={t.section4.secondLine}
                      />
                    ) : t.section4.secondLine,
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section4.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section4.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section4.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_4.jpg`],
                    alt: `Section 4 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 5 */}
              {t.section5 && (
                <TimelineSection
                  key="section5"
                  title={{
                    firstLine: isEditMode ? (
                      <EditableText
                        contentKey="tourmalines.section5.firstLine"
                        defaultValue={t.section5.firstLine}
                      />
                    ) : t.section5.firstLine,
                    secondLine: isEditMode ? (
                      <EditableText
                        contentKey="tourmalines.section5.secondLine"
                        defaultValue={t.section5.secondLine}
                      />
                    ) : t.section5.secondLine,
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section5.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section5.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section5.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_5.jpg`],
                    alt: `Section 5 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 6 */}
              {t.section6 && (
                <TimelineSection
                  key="section6"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section6.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section6.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section6.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_6.jpg`, `/images/tourmalines_sub_6a.jpg`],
                    alt: `Section 6 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 7 */}
              {t.section7 && (
                <TimelineSection
                  key="section7"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section7.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section7.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section7.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_7.jpg`],
                    alt: `Section 7 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 8 */}
              {t.section8 && (
                <TimelineSection
                  key="section8"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section8.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section8.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section8.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_8.jpg`],
                    alt: `Section 8 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 9 */}
              {t.section9 && (
                <TimelineSection
                  key="section9"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section9.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section9.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section9.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_9.jpg`],
                    alt: `Section 9 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 10 */}
              {t.section10 && (
                <TimelineSection
                  key="section10"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section10.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section10.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section10.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_10.jpg`],
                    alt: `Section 10 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 11 */}
              {t.section11 && (
                <TimelineSection
                  key="section11"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section11.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section11.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section11.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_11.jpg`, `/images/tourmalines_sub_11a.jpg`],
                    alt: `Section 11 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 12 */}
              {t.section12 && (
                <TimelineSection
                  key="section12"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section12.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section12.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section12.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_12.jpg`],
                    alt: `Section 12 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 13 */}
              {t.section13 && (
                <TimelineSection
                  key="section13"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section13.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section13.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section13.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_13.jpg`],
                    alt: `Section 13 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 14 */}
              {t.section14 && (
                <TimelineSection
                  key="section14"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section14.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section14.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section14.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_14.jpg`],
                    alt: `Section 14 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 15 */}
              {t.section15 && (
                <TimelineSection
                  key="section15"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section15.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section15.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section15.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_15.jpg`],
                    alt: `Section 15 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 16 */}
              {t.section16 && (
                <TimelineSection
                  key="section16"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section16.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section16.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section16.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_16.jpg`, `/images/tourmalines_sub_16a.jpg`],
                    alt: `Section 16 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 17 */}
              {t.section17 && (
                <TimelineSection
                  key="section17"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section17.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section17.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section17.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_17.jpg`],
                    alt: `Section 17 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 18 */}
              {t.section18 && (
                <TimelineSection
                  key="section18"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section18.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section18.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section18.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_18.jpg`],
                    alt: `Section 18 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 19 */}
              {t.section19 && (
                <TimelineSection
                  key="section19"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section19.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section19.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section19.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_19.jpg`],
                    alt: `Section 19 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 20 */}
              {t.section20 && (
                <TimelineSection
                  key="section20"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section20.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section20.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section20.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_20.jpg`],
                    alt: `Section 20 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 21 */}
              {t.section21 && (
                <TimelineSection
                  key="section21"
                  title={{
                    firstLine: t.section21.firstLine,
                    secondLine: t.section21.secondLine,
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section21.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section21.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section21.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_21.jpg`],
                    alt: `Section 21 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 22 */}
              {t.section22 && (
                <TimelineSection
                  key="section22"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section22.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section22.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section22.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_22.jpg`],
                    alt: `Section 22 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 23 */}
              {t.section23 && (
                <TimelineSection
                  key="section23"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section23.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section23.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section23.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [
                      `/images/tourmalines_sub_23.jpg`,
                      `/images/tourmalines_sub_23a.jpg`,
                      `/images/tourmalines_sub_23b.jpg`,
                      `/images/tourmalines_sub_23c.jpg`,
                      `/images/tourmalines_sub_23d.jpg`
                    ],
                    alt: `Section 23 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 24 */}
              {t.section24 && (
                <TimelineSection
                  key="section24"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section24.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section24.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section24.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_24.jpg`],
                    alt: `Section 24 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 25 */}
              {t.section25 && (
                <TimelineSection
                  key="section25"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section25.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section25.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section25.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_25.jpg`],
                    alt: `Section 25 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 26 */}
              {t.section26 && (
                <TimelineSection
                  key="section26"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section26.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section26.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section26.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_26.jpg`],
                    alt: `Section 26 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 27 */}
              {t.section27 && (
                <TimelineSection
                  key="section27"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section27.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section27.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section27.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_27.jpg`],
                    alt: `Section 27 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 28 */}
              {t.section28 && (
                <TimelineSection
                  key="section28"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section28.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section28.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section28.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_28.jpg`],
                    alt: `Section 28 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 29 */}
              {t.section29 && (
                <TimelineSection
                  key="section29"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section29.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section29.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section29.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [
                      `/images/tourmalines_sub_29.jpg`,
                      `/images/tourmalines_sub_29a.jpg`,
                      `/images/tourmalines_sub_29b.jpg`
                    ],
                    alt: `Section 29 Image`
                  }}
                  mediaPosition="right"
                  showLine={true}
                />
              )}

              {/* Section 30 */}
              {t.section30 && (
                <TimelineSection
                  key="section30"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section30.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section30.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section30.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_30.jpg`, `/images/tourmalines_sub_30a.jpg`],
                    alt: `Section 30 Image`
                  }}
                  mediaPosition="left"
                  showLine={true}
                />
              )}

              {/* Section 31 */}
              {t.section31 && (
                <TimelineSection
                  key="section31"
                  title={{
                    firstLine: "",
                    secondLine: " ",
                  }}
                  description={
                    isEditMode ? (
                      <div className="space-y-4">
                        {t.section31.description.map((paragraph: string, index: number) => (
                          <EditableText
                            key={index}
                            contentKey={`tourmalines.section31.description.${index}`}
                            defaultValue={paragraph}
                            multiline={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {t.section31.description.map((paragraph: string, index: number) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  }
                  media={{
                    type: 'image',
                    src: [`/images/tourmalines_sub_31.jpg`],
                    alt: `Section 31 Image`
                  }}
                  mediaPosition="right"
                  showLine={false}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tourmaline;